import React, { useState, useEffect, useCallback, useContext } from 'react';
import {
  CalendarOff, Plus, Check, X, Loader2, Save,
  ChevronLeft, ChevronRight, Filter, Download,
  Clock, CheckCircle2, XCircle, CircleDot, UserCheck,
} from 'lucide-react';
import { YearContext } from '../components/Layout/Layout';
import { leaveAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const TEACHER_ROLES = ['user', 'teacher'];
const COORD_ROLES   = ['management', 'coordinator', 'admin', 'super_admin'];

function isSelf(role)      { return TEACHER_ROLES.includes(role); }
function canApprove(role)  { return COORD_ROLES.includes(role); }
function isAdmin(role)     { return ['admin', 'super_admin'].includes(role); }

const STATUS_CONFIG = {
  pending:   { label: 'Pending',   cls: 'bg-amber-100  text-amber-700',   icon: CircleDot    },
  approved:  { label: 'Approved',  cls: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  rejected:  { label: 'Rejected',  cls: 'bg-red-100    text-red-700',     icon: XCircle      },
  cancelled: { label: 'Cancelled', cls: 'bg-slate-100  text-slate-500',   icon: X            },
};

function StatusBadge({ status }) {
  const c = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${c.cls}`}>
      <Icon size={11}/> {c.label}
    </span>
  );
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' });
}
function daysBetween(s, e) {
  return Math.round((new Date(e) - new Date(s)) / 86400000) + 1;
}
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

// ── Apply Modal ───────────────────────────────────────────────
function ApplyModal({ yearId, leaveTypes, onSave, onClose, role }) {
  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({
    leave_type_id: leaveTypes[0]?.id || '',
    start_date: today, end_date: today,
    reason: '', half_day: false, half_day_session: 'morning',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const totalDays = form.half_day ? 0.5
    : form.start_date && form.end_date && form.start_date <= form.end_date
      ? daysBetween(form.start_date, form.end_date)
      : 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.leave_type_id) return toast.error('Select a leave type');
    if (!form.start_date || !form.end_date) return toast.error('Select dates');
    if (form.start_date > form.end_date) return toast.error('End date must be after start');
    setSaving(true);
    try {
      await leaveAPI.apply({ ...form, academicYearId: yearId });
      toast.success('Leave application submitted');
      onSave();
    } catch (e) { toast.error(e.response?.data?.error || 'Submit failed'); }
    finally { setSaving(false); }
  };

  const inp = 'w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-400';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h2 className="font-display font-bold text-slate-800">Apply for Leave</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><X size={18}/></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">Leave Type *</label>
            <select value={form.leave_type_id} onChange={e => set('leave_type_id', e.target.value)} required className={inp}>
              <option value="">— Select —</option>
              {leaveTypes.map(t => <option key={t.id} value={t.id}>{t.name} ({t.code})</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="half_day" checked={form.half_day} onChange={e => set('half_day', e.target.checked)} className="rounded"/>
            <label htmlFor="half_day" className="text-sm text-slate-700 cursor-pointer">Half day</label>
            {form.half_day && (
              <select value={form.half_day_session} onChange={e => set('half_day_session', e.target.value)}
                className="ml-2 border border-slate-200 rounded-lg px-2 py-1 text-xs bg-white focus:outline-none">
                <option value="morning">Morning</option>
                <option value="afternoon">Afternoon</option>
              </select>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">From *</label>
              <input type="date" value={form.start_date} onChange={e => {
                set('start_date', e.target.value);
                if (form.half_day || e.target.value > form.end_date) set('end_date', e.target.value);
              }} required className={inp}/>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">To *</label>
              <input type="date" value={form.end_date} onChange={e => set('end_date', e.target.value)}
                min={form.start_date} disabled={form.half_day} required className={`${inp} ${form.half_day ? 'opacity-60' : ''}`}/>
            </div>
          </div>
          {totalDays > 0 && (
            <div className="text-xs font-medium text-primary-700 bg-primary-50 px-3 py-2 rounded-lg">
              {totalDays} day{totalDays !== 1 ? 's' : ''} requested
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">Reason</label>
            <textarea value={form.reason} onChange={e => set('reason', e.target.value)} rows={3}
              className={`${inp} resize-none`} placeholder="Reason for leave..."/>
          </div>
          <div className="flex justify-end gap-3 pt-1">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-600 bg-slate-100 rounded-xl hover:bg-slate-200">Cancel</button>
            <button type="submit" disabled={saving || totalDays === 0}
              className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-primary-600 rounded-xl hover:bg-primary-700 disabled:opacity-50">
              {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>}Submit
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Review Modal ──────────────────────────────────────────────
function ReviewModal({ app, onSave, onClose }) {
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(null); // 'approved'|'rejected'

  const act = async (action) => {
    setLoading(action);
    try {
      await leaveAPI.review(app.id, { action, comment });
      toast.success(action === 'approved' ? 'Leave approved' : 'Leave rejected');
      onSave();
    } catch (e) { toast.error(e.response?.data?.error || 'Action failed'); }
    finally { setLoading(null); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h2 className="font-display font-bold text-slate-800">Review Leave Request</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><X size={18}/></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="bg-slate-50 rounded-xl p-4 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">Employee</span><span className="font-semibold">{app.employee_name}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Leave Type</span><span className="font-medium">{app.leave_type_name}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Period</span><span>{fmtDate(app.start_date)} – {fmtDate(app.end_date)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Days</span><span className="font-semibold">{app.total_days}</span></div>
            {app.reason && <div><span className="text-slate-500">Reason: </span>{app.reason}</div>}
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">Comment <span className="text-slate-400 font-normal">(optional)</span></label>
            <textarea value={comment} onChange={e => setComment(e.target.value)} rows={3}
              className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
              placeholder="Add a note for the employee..."/>
          </div>
          <div className="flex gap-3 pt-1">
            <button onClick={() => act('rejected')} disabled={!!loading}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-red-700 bg-red-50 rounded-xl hover:bg-red-100 disabled:opacity-50">
              {loading === 'rejected' ? <Loader2 size={14} className="animate-spin"/> : <X size={14}/>}Reject
            </button>
            <button onClick={() => act('approved')} disabled={!!loading}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-xl hover:bg-emerald-700 disabled:opacity-50">
              {loading === 'approved' ? <Loader2 size={14} className="animate-spin"/> : <Check size={14}/>}Approve
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Application Row ───────────────────────────────────────────
function AppRow({ app, showEmployee, canReview, onReview, onCancel, cancelling }) {
  return (
    <tr className="hover:bg-slate-50">
      {showEmployee && (
        <td className="px-4 py-3">
          <div className="font-medium text-slate-700 text-sm">{app.employee_name}</div>
          <div className="text-xs text-slate-400">{app.designation}</div>
        </td>
      )}
      <td className="px-3 py-3">
        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{app.leave_code}</span>
        <div className="text-xs text-slate-500 mt-0.5">{app.leave_type_name}</div>
      </td>
      <td className="px-3 py-3 text-sm text-slate-600 whitespace-nowrap">{fmtDate(app.start_date)}</td>
      <td className="px-3 py-3 text-sm text-slate-600 whitespace-nowrap">{fmtDate(app.end_date)}</td>
      <td className="px-3 py-3 text-center font-semibold text-slate-700">{app.total_days}{app.half_day ? <span className="text-xs font-normal text-slate-400 ml-1">({app.half_day_session})</span> : ''}</td>
      <td className="px-3 py-3"><StatusBadge status={app.status}/></td>
      <td className="px-3 py-3 text-xs text-slate-400 max-w-[140px] truncate">{app.reason || '—'}</td>
      <td className="px-3 py-3">
        <div className="flex items-center gap-1">
          {canReview && app.status === 'pending' && (
            <button onClick={() => onReview(app)} className="flex items-center gap-1 px-2.5 py-1 bg-primary-50 text-primary-700 text-xs font-medium rounded-lg hover:bg-primary-100">
              <UserCheck size={12}/>Review
            </button>
          )}
          {!canReview && app.status === 'pending' && onCancel && (
            <button onClick={() => onCancel(app.id)} disabled={cancelling === app.id}
              className="flex items-center gap-1 px-2.5 py-1 bg-red-50 text-red-600 text-xs font-medium rounded-lg hover:bg-red-100 disabled:opacity-50">
              {cancelling === app.id ? <Loader2 size={11} className="animate-spin"/> : <X size={11}/>}Cancel
            </button>
          )}
          {app.review_comment && (
            <span title={app.review_comment} className="text-slate-400 cursor-help text-xs px-1">💬</span>
          )}
        </div>
      </td>
    </tr>
  );
}

// ── My Leaves Tab ─────────────────────────────────────────────
function MyLeavesTab({ yearId, role, leaveTypes }) {
  const [balances, setBalances]   = useState([]);
  const [apps,     setApps]       = useState([]);
  const [loading,  setLoading]    = useState(true);
  const [showApply,setShowApply]  = useState(false);
  const [cancelling, setCancelling] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [bR, aR] = await Promise.all([
        leaveAPI.getBalances({ academicYearId: yearId }),
        leaveAPI.getApplications({ academicYearId: yearId }),
      ]);
      setBalances(bR.data || []);
      setApps(aR.data || []);
    } catch { toast.error('Failed to load leave data'); }
    finally { setLoading(false); }
  }, [yearId]);
  useEffect(() => { load(); }, [load]);

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this leave application?')) return;
    setCancelling(id);
    try { await leaveAPI.cancel(id); toast.success('Cancelled'); load(); }
    catch (e) { toast.error(e.response?.data?.error || 'Cancel failed'); }
    finally { setCancelling(null); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 size={24} className="animate-spin text-primary-500"/></div>;

  const pending  = apps.filter(a => a.status === 'pending').length;
  const approved = apps.filter(a => a.status === 'approved').length;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex gap-4 text-sm text-slate-500">
          <span><b className="text-amber-600">{pending}</b> pending</span>
          <span><b className="text-emerald-600">{approved}</b> approved</span>
        </div>
        <button onClick={() => setShowApply(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-xl hover:bg-primary-700">
          <Plus size={15}/>Apply for Leave
        </button>
      </div>

      {/* Balance cards */}
      {balances.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
          {balances.map(b => {
            const avail = b.total_days - b.used_days - b.pending_days;
            const pct   = b.total_days > 0 ? Math.max(0, (avail / b.total_days) * 100) : 0;
            return (
              <div key={b.id} className="bg-white rounded-2xl border border-slate-200 p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="text-lg font-display font-bold text-slate-800">{avail}</div>
                    <div className="text-xs text-slate-400">/ {b.total_days} days</div>
                  </div>
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-primary-100 text-primary-700">{b.code}</span>
                </div>
                <div className="text-xs font-medium text-slate-600 truncate mb-2">{b.type_name}</div>
                <div className="w-full bg-slate-100 rounded-full h-1.5">
                  <div className="bg-primary-500 h-1.5 rounded-full transition-all" style={{ width: `${pct}%` }}/>
                </div>
                {b.pending_days > 0 && (
                  <div className="text-[10px] text-amber-600 mt-1">{b.pending_days} pending</div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {balances.length === 0 && (
        <div className="text-xs text-slate-400 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3">
          No leave balance configured yet. Contact admin to initialize your leave balance.
        </div>
      )}

      {/* Applications table */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
        <div className="px-4 py-3 border-b"><h3 className="font-semibold text-slate-700 text-sm">My Applications</h3></div>
        {apps.length === 0 ? (
          <div className="text-center py-12"><CalendarOff size={32} className="mx-auto text-slate-300 mb-2"/><p className="text-slate-500 text-sm">No leave applications yet</p></div>
        ) : (
          <table className="w-full text-sm min-w-[700px]">
            <thead className="bg-slate-50 text-xs text-slate-500 font-semibold">
              <tr>
                <th className="px-3 py-2.5 text-left">Type</th>
                <th className="px-3 py-2.5 text-left">From</th>
                <th className="px-3 py-2.5 text-left">To</th>
                <th className="px-3 py-2.5 text-center">Days</th>
                <th className="px-3 py-2.5 text-left">Status</th>
                <th className="px-3 py-2.5 text-left">Reason</th>
                <th className="px-3 py-2.5 w-20"/>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {apps.map(app => (
                <AppRow key={app.id} app={app} showEmployee={false} canReview={false}
                  onCancel={handleCancel} cancelling={cancelling}/>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showApply && (
        <ApplyModal yearId={yearId} leaveTypes={leaveTypes} role={role}
          onSave={() => { setShowApply(false); load(); }}
          onClose={() => setShowApply(false)}/>
      )}
    </div>
  );
}

// ── Pending Approvals Tab ─────────────────────────────────────
function PendingTab({ yearId }) {
  const [apps,    setApps]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await leaveAPI.getApplications({ academicYearId: yearId, status: 'pending' });
      setApps(r.data || []);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [yearId]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-16"><Loader2 size={24} className="animate-spin text-primary-500"/></div>;

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500">{apps.length} pending application{apps.length !== 1 ? 's' : ''}</p>
      {apps.length === 0 ? (
        <div className="text-center py-16 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
          <Check size={36} className="mx-auto text-emerald-400 mb-3"/>
          <p className="text-slate-600 font-medium">All caught up — no pending requests</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm min-w-[780px]">
            <thead className="bg-slate-50 text-xs text-slate-500 font-semibold">
              <tr>
                <th className="px-4 py-2.5 text-left">Employee</th>
                <th className="px-3 py-2.5 text-left">Type</th>
                <th className="px-3 py-2.5 text-left">From</th>
                <th className="px-3 py-2.5 text-left">To</th>
                <th className="px-3 py-2.5 text-center">Days</th>
                <th className="px-3 py-2.5 text-left">Status</th>
                <th className="px-3 py-2.5 text-left">Reason</th>
                <th className="px-3 py-2.5 w-24"/>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {apps.map(app => (
                <AppRow key={app.id} app={app} showEmployee canReview
                  onReview={setReviewing}/>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {reviewing && (
        <ReviewModal app={reviewing}
          onSave={() => { setReviewing(null); load(); }}
          onClose={() => setReviewing(null)}/>
      )}
    </div>
  );
}

// ── All Applications Tab ──────────────────────────────────────
function AllAppsTab({ yearId, canReview }) {
  const [apps,      setApps]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [reviewing, setReviewing] = useState(null);
  const [filters,   setFilters]   = useState({ status: '', month: '', year: new Date().getFullYear() });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { academicYearId: yearId };
      if (filters.status) params.status = filters.status;
      if (filters.month)  { params.month = filters.month; params.year = filters.year; }
      const r = await leaveAPI.getApplications(params);
      setApps(r.data || []);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [yearId, filters]);
  useEffect(() => { load(); }, [load]);

  const sf = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <select value={filters.status} onChange={e => sf('status', e.target.value)}
          className="border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-400">
          <option value="">All Statuses</option>
          {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={filters.month} onChange={e => sf('month', e.target.value)}
          className="border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-400">
          <option value="">All Months</option>
          {MONTHS.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
        </select>
        {filters.month && (
          <select value={filters.year} onChange={e => sf('year', parseInt(e.target.value))}
            className="border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-400">
            {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        )}
        <span className="text-sm text-slate-400">{apps.length} record{apps.length !== 1 ? 's' : ''}</span>
      </div>

      {loading ? <div className="flex justify-center py-12"><Loader2 size={22} className="animate-spin text-primary-500"/></div>
      : apps.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
          <Filter size={32} className="mx-auto text-slate-300 mb-2"/>
          <p className="text-slate-500 text-sm">No applications match these filters</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm min-w-[820px]">
            <thead className="bg-slate-50 text-xs text-slate-500 font-semibold">
              <tr>
                <th className="px-4 py-2.5 text-left">Employee</th>
                <th className="px-3 py-2.5 text-left">Type</th>
                <th className="px-3 py-2.5 text-left">From</th>
                <th className="px-3 py-2.5 text-left">To</th>
                <th className="px-3 py-2.5 text-center">Days</th>
                <th className="px-3 py-2.5 text-left">Status</th>
                <th className="px-3 py-2.5 text-left">Reason</th>
                <th className="px-3 py-2.5 w-24"/>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {apps.map(app => (
                <AppRow key={app.id} app={app} showEmployee canReview={canReview}
                  onReview={setReviewing}/>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {reviewing && (
        <ReviewModal app={reviewing}
          onSave={() => { setReviewing(null); load(); }}
          onClose={() => setReviewing(null)}/>
      )}
    </div>
  );
}

// ── Calendar Tab ──────────────────────────────────────────────
function CalendarTab({ yearId }) {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year,  setYear]  = useState(now.getFullYear());
  const [data,  setData]  = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    leaveAPI.getCalendar({ academicYearId: yearId, month, year })
      .then(r => setData(r.data || []))
      .catch(() => toast.error('Failed to load calendar'))
      .finally(() => setLoading(false));
  }, [yearId, month, year]);

  const prevMonth = () => { if (month === 1) { setMonth(12); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 12) { setMonth(1); setYear(y => y + 1); } else setMonth(m => m + 1); };

  // Build calendar grid
  const firstDay = new Date(year, month - 1, 1).getDay(); // 0=Sun
  const daysInMonth = new Date(year, month, 0).getDate();

  // Map: date string → list of employees on leave
  const leaveMap = {};
  for (const app of data) {
    const s = new Date(app.start_date);
    const e = new Date(app.end_date);
    for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
      const key = d.toISOString().split('T')[0];
      const [dy, dm] = key.split('-').map(Number);
      if (dm !== month || dy !== year) continue;
      if (!leaveMap[key]) leaveMap[key] = [];
      leaveMap[key].push({ name: app.employee_name, code: app.leave_code });
    }
  }

  const todayStr = new Date().toISOString().split('T')[0];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <button onClick={prevMonth} className="p-2 rounded-xl hover:bg-slate-100 text-slate-500"><ChevronLeft size={18}/></button>
        <h3 className="font-display font-bold text-slate-800 text-lg min-w-[180px] text-center">{MONTHS[month-1]} {year}</h3>
        <button onClick={nextMonth} className="p-2 rounded-xl hover:bg-slate-100 text-slate-500"><ChevronRight size={18}/></button>
      </div>

      {loading ? <div className="flex justify-center py-12"><Loader2 size={22} className="animate-spin text-primary-500"/></div> : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          {/* Day headers */}
          <div className="grid grid-cols-7 border-b">
            {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
              <div key={d} className="text-center text-xs font-semibold text-slate-500 py-2.5 bg-slate-50">{d}</div>
            ))}
          </div>
          {/* Dates grid */}
          <div className="grid grid-cols-7">
            {/* Empty cells before first day */}
            {Array.from({ length: firstDay }).map((_, i) => (
              <div key={`e${i}`} className="min-h-[90px] p-1 border-b border-r border-slate-100 bg-slate-50/40"/>
            ))}
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
              const dateStr = `${year}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
              const leaves  = leaveMap[dateStr] || [];
              const isToday = dateStr === todayStr;
              return (
                <div key={day} className={`min-h-[90px] p-1.5 border-b border-r border-slate-100 ${isToday ? 'bg-primary-50' : ''}`}>
                  <div className={`text-xs font-semibold mb-1 w-6 h-6 flex items-center justify-center rounded-full
                    ${isToday ? 'bg-primary-600 text-white' : 'text-slate-600'}`}>{day}</div>
                  <div className="space-y-0.5">
                    {leaves.slice(0, 3).map((l, i) => (
                      <div key={i} className="text-[10px] bg-amber-100 text-amber-800 rounded px-1 py-0.5 truncate">
                        {l.name.split(' ')[0]} <span className="opacity-60">({l.code})</span>
                      </div>
                    ))}
                    {leaves.length > 3 && (
                      <div className="text-[10px] text-slate-400">+{leaves.length - 3} more</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Legend */}
      {data.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">On Leave This Month</h4>
          <div className="flex flex-wrap gap-2">
            {[...new Set(data.map(a => a.employee_name))].sort().map(name => (
              <span key={name} className="text-xs bg-amber-50 text-amber-800 px-2 py-1 rounded-lg border border-amber-100">{name}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Balances Tab (admin) ──────────────────────────────────────
function BalancesTab({ yearId }) {
  const [balances,  setBalances]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [initing,   setIniting]   = useState(false);
  const [editId,    setEditId]    = useState(null);
  const [editVal,   setEditVal]   = useState('');
  const [saving,    setSaving]    = useState(false);
  const [search,    setSearch]    = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await leaveAPI.getBalances({ academicYearId: yearId });
      setBalances(r.data || []);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [yearId]);
  useEffect(() => { load(); }, [load]);

  const handleInit = async () => {
    if (!window.confirm('Initialize leave balances for all active staff? Existing records won\'t be overwritten.')) return;
    setIniting(true);
    try { const r = await leaveAPI.initBalances({ academicYearId: yearId }); toast.success(r.data.message); load(); }
    catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
    finally { setIniting(false); }
  };

  const handleSave = async (id) => {
    setSaving(true);
    try { await leaveAPI.adjustBalance(id, { total_days: parseFloat(editVal) }); toast.success('Balance updated'); setEditId(null); load(); }
    catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  // Group by employee
  const filtered = search.trim()
    ? balances.filter(b => b.employee_name.toLowerCase().includes(search.toLowerCase()) || b.employee_code?.toLowerCase().includes(search.toLowerCase()))
    : balances;

  const byEmp = filtered.reduce((acc, b) => {
    if (!acc[b.employee_id]) acc[b.employee_id] = { name: b.employee_name, code: b.employee_code, dept: b.department, balances: [] };
    acc[b.employee_id].balances.push(b);
    return acc;
  }, {});

  if (loading) return <div className="flex justify-center py-16"><Loader2 size={24} className="animate-spin text-primary-500"/></div>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search employee..."
          className="border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 w-64"/>
        <button onClick={handleInit} disabled={initing}
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white text-sm font-medium rounded-xl hover:bg-amber-600 disabled:opacity-50">
          {initing ? <Loader2 size={14} className="animate-spin"/> : <Plus size={14}/>}Initialize Balances
        </button>
      </div>

      {Object.keys(byEmp).length === 0 ? (
        <div className="text-center py-14 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
          <CalendarOff size={36} className="mx-auto text-slate-300 mb-3"/>
          <p className="text-slate-600 font-medium">No balances found</p>
          <p className="text-slate-400 text-sm mt-1">Click "Initialize Balances" to create records for all staff.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500 font-semibold">
              <tr>
                <th className="px-4 py-2.5 text-left">Employee</th>
                <th className="px-3 py-2.5 text-left">Leave Type</th>
                <th className="px-3 py-2.5 text-right">Total</th>
                <th className="px-3 py-2.5 text-right">Used</th>
                <th className="px-3 py-2.5 text-right">Pending</th>
                <th className="px-3 py-2.5 text-right">Available</th>
                <th className="px-3 py-2.5 w-16"/>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {Object.values(byEmp).map((emp, ei) =>
                emp.balances.map((b, bi) => (
                  <tr key={b.id} className="hover:bg-slate-50">
                    {bi === 0 && (
                      <td className="px-4 py-2.5" rowSpan={emp.balances.length}>
                        <div className="font-medium text-slate-700">{emp.name}</div>
                        <div className="text-xs text-slate-400">{emp.code} · {emp.dept}</div>
                      </td>
                    )}
                    <td className="px-3 py-2"><span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{b.code}</span> {b.type_name}</td>
                    <td className="px-3 py-2 text-right">
                      {editId === b.id ? (
                        <input type="number" min="0" step="0.5" value={editVal} onChange={e => setEditVal(e.target.value)}
                          className="w-16 border border-primary-300 rounded-lg px-2 py-0.5 text-sm text-right focus:outline-none focus:ring-1 focus:ring-primary-400"/>
                      ) : <span className="font-semibold">{b.total_days}</span>}
                    </td>
                    <td className="px-3 py-2 text-right text-slate-500">{b.used_days}</td>
                    <td className="px-3 py-2 text-right text-amber-600">{b.pending_days}</td>
                    <td className="px-3 py-2 text-right font-semibold text-emerald-600">{b.available_days}</td>
                    <td className="px-3 py-2 text-right">
                      {editId === b.id ? (
                        <div className="flex gap-1 justify-end">
                          <button onClick={() => handleSave(b.id)} disabled={saving}
                            className="p-1 rounded text-emerald-600 hover:bg-emerald-50">{saving ? <Loader2 size={13} className="animate-spin"/> : <Check size={13}/>}</button>
                          <button onClick={() => setEditId(null)} className="p-1 rounded text-slate-400 hover:bg-slate-100"><X size={13}/></button>
                        </div>
                      ) : (
                        <button onClick={() => { setEditId(b.id); setEditVal(b.total_days); }}
                          className="text-xs text-primary-600 hover:underline">Edit</button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Leave Types Tab (admin) ───────────────────────────────────
function LeaveTypesTab() {
  const [types,   setTypes]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [form,    setForm]    = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [deleting,setDeleting]= useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await leaveAPI.getTypes(); setTypes(r.data || []); }
    catch { toast.error('Failed'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const BLANK = { code:'', name:'', default_days_per_year: 12, is_paid: true, requires_docs: false, can_carry_forward: false, sort_order: 0 };
  const sf = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const inp = 'w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-400';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.code?.trim() || !form.name?.trim()) return toast.error('Code and name required');
    setSaving(true);
    try {
      if (form.id) await leaveAPI.updateType(form.id, form);
      else         await leaveAPI.createType(form);
      toast.success(form.id ? 'Updated' : 'Created');
      setForm(null); load();
    } catch (e) { toast.error(e.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 size={24} className="animate-spin text-primary-500"/></div>;

  return (
    <div className="space-y-4 max-w-2xl">
      {!form && (
        <button onClick={() => setForm({ ...BLANK })}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-xl hover:bg-primary-700">
          <Plus size={15}/>Add Leave Type
        </button>
      )}
      {form && (
        <form onSubmit={handleSubmit} className="bg-primary-50 border border-primary-100 rounded-2xl p-5 space-y-4">
          <h4 className="font-semibold text-primary-800 text-sm">{form.id ? 'Edit Leave Type' : 'New Leave Type'}</h4>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-xs font-medium text-slate-600 mb-1">Code *</label>
              <input value={form.code} onChange={e => sf('code', e.target.value.toUpperCase())} required maxLength={10} placeholder="CL" className={inp}/></div>
            <div><label className="block text-xs font-medium text-slate-600 mb-1">Name *</label>
              <input value={form.name} onChange={e => sf('name', e.target.value)} required placeholder="Casual Leave" className={inp}/></div>
            <div><label className="block text-xs font-medium text-slate-600 mb-1">Days/Year</label>
              <input type="number" min="0" step="0.5" value={form.default_days_per_year} onChange={e => sf('default_days_per_year', e.target.value)} className={inp}/></div>
            <div><label className="block text-xs font-medium text-slate-600 mb-1">Sort Order</label>
              <input type="number" min="0" value={form.sort_order} onChange={e => sf('sort_order', e.target.value)} className={inp}/></div>
          </div>
          <div className="flex flex-wrap gap-4">
            {[['is_paid','Paid Leave'],['requires_docs','Requires Documents'],['can_carry_forward','Carry Forward']].map(([k, l]) => (
              <label key={k} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" checked={!!form[k]} onChange={e => sf(k, e.target.checked)} className="rounded"/>
                {l}
              </label>
            ))}
          </div>
          <div className="flex gap-3">
            <button type="submit" disabled={saving}
              className="flex items-center gap-2 px-5 py-2 bg-primary-600 text-white text-sm font-medium rounded-xl hover:bg-primary-700 disabled:opacity-50">
              {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>}Save
            </button>
            <button type="button" onClick={() => setForm(null)}
              className="px-4 py-2 text-sm text-slate-600 bg-white border border-slate-200 rounded-xl hover:bg-slate-50">Cancel</button>
          </div>
        </form>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs text-slate-500 font-semibold">
            <tr>
              <th className="px-4 py-2.5 text-left">Code</th>
              <th className="px-3 py-2.5 text-left">Name</th>
              <th className="px-3 py-2.5 text-right">Days/Year</th>
              <th className="px-3 py-2.5 text-center">Paid</th>
              <th className="px-3 py-2.5 text-center">Docs</th>
              <th className="px-3 py-2.5 text-center">C/F</th>
              <th className="px-3 py-2.5 w-20"/>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {types.map(t => (
              <tr key={t.id} className="hover:bg-slate-50">
                <td className="px-4 py-2.5"><span className="font-bold text-slate-700">{t.code}</span></td>
                <td className="px-3 py-2.5">{t.name}</td>
                <td className="px-3 py-2.5 text-right font-semibold">{t.default_days_per_year}</td>
                <td className="px-3 py-2.5 text-center">{t.is_paid ? <Check size={14} className="mx-auto text-emerald-500"/> : <X size={14} className="mx-auto text-slate-300"/>}</td>
                <td className="px-3 py-2.5 text-center">{t.requires_docs ? <Check size={14} className="mx-auto text-emerald-500"/> : <X size={14} className="mx-auto text-slate-300"/>}</td>
                <td className="px-3 py-2.5 text-center">{t.can_carry_forward ? <Check size={14} className="mx-auto text-emerald-500"/> : <X size={14} className="mx-auto text-slate-300"/>}</td>
                <td className="px-3 py-2.5 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => setForm({ ...t, is_paid: !!t.is_paid, requires_docs: !!t.requires_docs, can_carry_forward: !!t.can_carry_forward })}
                      className="text-xs text-primary-600 hover:underline px-1">Edit</button>
                    <button onClick={async () => { if (!window.confirm('Deactivate this type?')) return; setDeleting(t.id); try { await leaveAPI.deleteType(t.id); toast.success('Deactivated'); load(); } catch { toast.error('Failed'); } finally { setDeleting(null); } }}
                      disabled={deleting === t.id}
                      className="text-xs text-red-500 hover:underline px-1">
                      {deleting === t.id ? <Loader2 size={11} className="animate-spin"/> : 'Remove'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────
export default function LeaveManagement() {
  const { user }   = useAuth();
  const { yearId } = useContext(YearContext);
  const role       = user?.role;
  const admin      = isAdmin(role);
  const approver   = canApprove(role);
  const self       = isSelf(role);

  const [leaveTypes, setLeaveTypes] = useState([]);
  useEffect(() => {
    leaveAPI.getTypes().then(r => setLeaveTypes((r.data || []).filter(t => t.is_active))).catch(() => {});
  }, []);

  const tabs = [
    { key: 'mine',     label: 'My Leaves',          show: true },
    { key: 'pending',  label: 'Pending Approvals',  show: approver },
    { key: 'all',      label: 'All Applications',   show: approver },
    { key: 'calendar', label: 'Calendar',            show: true },
    { key: 'balances', label: 'Balances',            show: admin },
    { key: 'types',    label: 'Leave Types',         show: admin },
  ].filter(t => t.show);

  const [activeTab, setActiveTab] = useState('mine');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
          <CalendarOff size={24} className="text-primary-600"/> Leave Management
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">Apply for leave, review requests, and track balances</p>
      </div>

      <div className="flex flex-wrap gap-1 bg-slate-100 p-1.5 rounded-2xl w-fit">
        {tabs.map(({ key, label }) => (
          <button key={key} onClick={() => setActiveTab(key)}
            className={`px-3.5 py-2 rounded-xl text-sm font-medium transition whitespace-nowrap
              ${activeTab === key ? 'bg-white text-primary-700 shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-white/60'}`}>
            {label}
          </button>
        ))}
      </div>

      <div>
        {activeTab === 'mine'     && <MyLeavesTab yearId={yearId} role={role} leaveTypes={leaveTypes}/>}
        {activeTab === 'pending'  && <PendingTab  yearId={yearId}/>}
        {activeTab === 'all'      && <AllAppsTab  yearId={yearId} canReview={approver}/>}
        {activeTab === 'calendar' && <CalendarTab yearId={yearId}/>}
        {activeTab === 'balances' && <BalancesTab yearId={yearId}/>}
        {activeTab === 'types'    && <LeaveTypesTab/>}
      </div>
    </div>
  );
}
